import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from "axios";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import Sheet from '@mui/joy/Sheet';
import CssBaseline from '@mui/joy/CssBaseline';
import Typography from '@mui/joy/Typography';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Link from '@mui/joy/Link';
import { useTranslation } from 'react-i18next';
import { useModal } from '../context/ModalContext';

const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login = ({ onClose }) => {
  console.log('Login rendered');
  const { t } = useTranslation();
  const { showRegister } = useModal();
  //console.log('Google Client ID:', clientId);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const { username, password } = formData;

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/login/`, {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const userData = response.data;
      setMessage(t('login.welcomeBack', { name: userData.name }));
      localStorage.setItem('user', JSON.stringify(userData));
      // Dispatch custom event
      window.dispatchEvent(new Event('userLoggedIn'));
      if (onClose) onClose();
      if (window.location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || t('login.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/google-login/`, {
        token: credentialResponse.credential,
      });
      localStorage.setItem('user', JSON.stringify(response.data));
      // Dispatch custom event
      window.dispatchEvent(new Event('userLoggedIn'));
      if (onClose) onClose();
      if (window.location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Google login failed:', error);
    }
  };

  const handleGoogleFailure = () => {
    console.error('Google Login Failed');
  };

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <main>
        <CssBaseline />
        <Sheet sx={{ width: 300, mx: 'auto', my: 4, py: 3, px: 2, display: 'flex', flexDirection: 'column', gap: 2, borderRadius: 'sm', boxShadow: 'md' }} variant="outlined">
          <Typography level="h4" component="h1"><b>{t('login.title')}</b></Typography>
          <form onSubmit={onSubmit}>
            <FormControl>
              <FormLabel>{t('login.username')}</FormLabel>
              <Input
                name="username"
                type="text"
                placeholder={t('login.usernamePlaceholder')}
                value={username}
                onChange={onChange}
                required
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t('login.password')}</FormLabel>
              <Input
                name="password"
                type="password"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={onChange}
                required
              />
            </FormControl>
            <Button type="submit" sx={{ mt: 1 }} disabled={loading}>
              {loading ? t('login.loggingIn') : t('login.loginButton')}
            </Button>
          </form>
          {message && <Typography color="success">{message}</Typography>}
          {error && <Typography color="error">{error}</Typography>}
          <Typography 
            endDecorator={
              <button
                style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', padding: 0 }}
                onClick={() => { console.log('Register button clicked'); showRegister(); }}
                type="button"
              >
                {t('login.register')}
              </button>
            }
            fontSize="sm" 
            sx={{ alignSelf: 'center' }}
          >
            {t('login.noAccount')}
          </Typography>
          
          {/* Google login option */}
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleFailure}
          />
          <Typography fontSize="sm" sx={{ alignSelf: 'center', mt: 1 }}>
            {t('login.agreeToTerms')}{' '}
            <Link component={RouterLink} to="/terms-of-use" fontSize="sm">{t('login.termsOfUse')}</Link>
            {' '}{t('login.and')}{' '}
            <Link component={RouterLink} to="/privacy-policy" fontSize="sm">{t('login.privacyPolicy')}</Link>.
          </Typography>
        </Sheet>
      </main>
    </GoogleOAuthProvider>
  );
};

export default Login;